import { Children, cloneElement, isValidElement, useState } from 'react';

import { cn } from '@utils/cn';

import { Icon } from '@ui-component-library/components/Icon';

import {
  accordionClasses,
  accordionContentClasses,
  accordionHeaderClasses,
  accordionIconClasses
} from './Accordion.styles';
import type {
  AccordionContentProps,
  AccordionHeaderProps,
  AccordionProps
} from './Accordion.types';

export const Accordion = ({
  children,
  isOpen = false,
  onOpenChange,
  className,
  underline = true,
  iconColor,
  iconColorWhenOpen,
  iconPosition = 'right',
  ...props
}: AccordionProps) => {
  const [isOpenState, setIsOpenState] = useState(isOpen);
  const isControlled = onOpenChange !== undefined;
  const open = isControlled ? isOpen : isOpenState;

  const setOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange(next);
    } else {
      setIsOpenState(next);
    }
  };

  const childrenWithProps = Children.map(children, (child) =>
    isValidElement(child)
      ? cloneElement(child, {
          isOpen: open,
          setIsOpen: setOpen,
          ...(iconColor !== null && { iconColor }),
          ...(iconColorWhenOpen !== undefined && { iconColorWhenOpen }),
          iconPosition
        } as AccordionHeaderProps)
      : child
  );

  return (
    <div className={cn(accordionClasses({ underline, iconPosition }), className)} {...props}>
      {childrenWithProps}
    </div>
  );
};

const Header = ({
  children,
  action,
  isOpen = false,
  setIsOpen,
  className,
  iconColor,
  iconColorWhenOpen,
  iconPosition = 'right',
  ...props
}: AccordionHeaderProps) => {
  const handleToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsOpen?.(!isOpen);
  };
  const resolvedIconColor =
    isOpen && iconColorWhenOpen !== undefined ? iconColorWhenOpen : iconColor;

  const renderedChildren =
    typeof children === 'function' ? children({ isOpen, setIsOpen }) : children;

  return (
    <div
      className={cn(accordionHeaderClasses({ iconPosition }), className)}
      onClick={(e) => handleToggle(e)}
      {...props}
    >
      {iconPosition === 'left' && (
        <span style={{ color: resolvedIconColor }}>
          <Icon
            name={isOpen ? 'Minus' : 'PlusSimple'}
            size="small"
            className={cn(accordionIconClasses({ isOpen }))}
          />
        </span>
      )}
      <div className="flex w-full items-center justify-between gap-2">
        {renderedChildren}
        {action !== undefined && action !== null && (
          <span className="shrink-0" onClick={(e) => e.stopPropagation()} role="presentation">
            {action}
          </span>
        )}
      </div>

      {iconPosition === 'right' && (
        <span style={{ color: resolvedIconColor }}>
          <Icon
            name="ChevronDownSimple"
            size="medium"
            className={cn(accordionIconClasses({ isOpen }))}
          />
        </span>
      )}
    </div>
  );
};

const Content = ({
  children,
  isOpen = false,
  className,
  iconPosition = 'right',
  ...props
}: AccordionContentProps) => {
  return (
    <div className={cn(accordionContentClasses({ isOpen, iconPosition }), className)} {...props}>
      {children}
    </div>
  );
};

Accordion.Header = Header;
Accordion.Content = Content;
